import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { LocalizationService } from '../../core/localization/localization.service';
import { FormFieldErrorsState } from '../../shared/design-system/form-field-errors';
import { getPaymentProviderUiProfile } from './payment-provider-ui-profiles';
import {
  ParsianProviderSettings,
  isParsianProviderSettingsValid,
  parseParsianProviderSettings,
  serializeParsianProviderSettings
} from './payment-provider-settings.models';
import { ParsianProviderSettingsFormComponent } from './parsian-provider-settings-form.component';

@Component({
  standalone: true,
  selector: 'ain-payment-provider-settings-panel',
  imports: [ParsianProviderSettingsFormComponent],
  template: `
    @if (profile().settingsKind === 'redirect-ipg' && profile().gatewayFamily === 'parsian-pec') {
      <ain-parsian-provider-settings-form
        [settings]="parsianSettings"
        [fieldErrors]="fieldErrors"
        [validateOnBlur]="validateOnBlur"
        (settingsChange)="onParsianSettingsChange($event)" />
    } @else if (profile().settingsKind === 'internal') {
      <p class="muted">{{ t('payments.providers.gateway.internalNotice') }}</p>
    } @else {
      <p class="muted">{{ t('payments.providers.gateway.notConfigurable') }}</p>
    }
  `,
  styles: [`
    .muted { margin: 0; color: #64748b; font-size: 13px; line-height: 1.5; }
  `]
})
export class PaymentProviderSettingsPanelComponent implements OnChanges {
  private readonly localization = inject(LocalizationService);

  @Input({ required: true }) providerKey = '';
  @Input() settingsJson: string | null = null;
  @Input() fieldErrors = new FormFieldErrorsState();
  @Input() validateOnBlur = true;

  @Output() settingsJsonChange = new EventEmitter<string | null>();

  parsianSettings: ParsianProviderSettings = parseParsianProviderSettings(null);

  profile(): ReturnType<typeof getPaymentProviderUiProfile> {
    return getPaymentProviderUiProfile(this.providerKey);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['providerKey'] || changes['settingsJson']) {
      this.syncParsianSettingsFromInputs();
    }
  }

  private syncParsianSettingsFromInputs(): void {
    if (this.profile().gatewayFamily === 'parsian-pec') {
      this.parsianSettings = parseParsianProviderSettings(this.settingsJson);
    }
  }

  /** Read-only check for template bindings (e.g. disabled save). */
  isGatewaySettingsSatisfiedForSave(willEnable: boolean): boolean {
    if (!willEnable) {
      return true;
    }

    if (this.profile().gatewayFamily === 'parsian-pec') {
      return isParsianProviderSettingsValid(this.parsianSettings);
    }

    return true;
  }

  onParsianSettingsChange(settings: ParsianProviderSettings): void {
    this.parsianSettings = settings;
    this.settingsJsonChange.emit(serializeParsianProviderSettings(settings));
  }

  /** Returns false when gateway-specific validation fails. */
  validateGatewaySettings(willEnable: boolean): boolean {
    if (!willEnable) {
      return true;
    }

    if (this.profile().gatewayFamily === 'parsian-pec') {
      return this.validateParsianWithFieldErrors();
    }

    return true;
  }

  private validateParsianWithFieldErrors(): boolean {
    const value = this.parsianSettings.merchantLoginAccount.trim();
    if (!value) {
      this.fieldErrors.setField('merchantLoginAccount', 'payments.providers.gateway.parsian.errors.merchantRequired');
      return false;
    }
    if (!isParsianProviderSettingsValid(this.parsianSettings)) {
      this.fieldErrors.setField('merchantLoginAccount', 'payments.providers.gateway.parsian.errors.merchantInvalid');
      return false;
    }
    this.fieldErrors.clear('merchantLoginAccount');
    return true;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
